import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { verifyAdmin, getContacts, getPartners, createPartner, deletePartner, getSettings, updateSettings, getHotels, createHotel, deleteHotel, getGallery, uploadGalleryImage, deleteGalleryImage, reorderGallery, galleryImageUrl, getTestimonialsAdmin, approveTestimonial, deleteTestimonial } from "@/lib/api";
import { ArrowLeft, Trash2, Plus, Lock, Loader2, Mail, Building2, Wifi, Save, Hotel as HotelIcon, Upload, ArrowUp, ArrowDown, Image as ImageIcon, Star, Check } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("pt_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState("partners");
  const [partners, setPartners] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [apCount, setApCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);
  const [savingAp, setSavingAp] = useState(false);
  const [form, setForm] = useState({ name: "", logo_url: "", website: "", category: "" });
  const [hotelForm, setHotelForm] = useState({ name: "", logo_url: "", website: "" });
  const [gallery, setGallery] = useState([]);
  const [galleryCaption, setGalleryCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  const load = async (tk) => {
    try {
      const [p, c, s, h, gal, rev] = await Promise.all([getPartners(), getContacts(tk), getSettings(), getHotels(), getGallery(), getTestimonialsAdmin(tk)]);
      setPartners(p);
      setContacts(c);
      setApCount(s.access_points || 0);
      setProjectsCount(s.projects || 0);
      setHotels(h);
      setGallery(gal);
      setTestimonials(rev);
    } catch { /* ignore */ }
  };

  const removeTestimonial = async (id) => {
    try {
      await deleteTestimonial(id, token);
      toast.success("Bewertung gelöscht");
      load(token);
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const approveReview = async (id) => {
    try {
      await approveTestimonial(id, token);
      toast.success("Bewertung freigegeben");
      load(token);
    } catch {
      toast.error("Freigabe fehlgeschlagen");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", galleryCaption);
      await uploadGalleryImage(fd, token);
      setGalleryCaption("");
      toast.success("Bild hochgeladen");
      load(token);
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryItem = async (id) => {
    try {
      await deleteGalleryImage(id, token);
      toast.success("Bild gelöscht");
      load(token);
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const moveItem = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= gallery.length) return;
    const arr = [...gallery];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setGallery(arr);
    try {
      await reorderGallery(arr.map((g) => g.id), token);
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
      load(token);
    }
  };

  const saveAp = async () => {
    setSavingAp(true);
    try {
      await updateSettings({ projects: Number(projectsCount) || 0, access_points: Number(apCount) || 0 }, token);
      toast.success("Kennzahlen gespeichert");
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingAp(false);
    }
  };

  const addHotel = async (e) => {
    e.preventDefault();
    if (!hotelForm.name.trim()) return;
    try {
      await createHotel(hotelForm, token);
      setHotelForm({ name: "", logo_url: "", website: "" });
      toast.success("Hotel hinzugefügt");
      load(token);
    } catch {
      toast.error("Fehler beim Speichern");
    }
  };

  const removeHotel = async (id) => {
    try {
      await deleteHotel(id, token);
      toast.success("Hotel gelöscht");
      load(token);
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  useEffect(() => {
    if (token) tryLogin(token, true);
    // eslint-disable-next-line
  }, []);

  const tryLogin = async (tk, silent = false) => {
    setChecking(true);
    try {
      await verifyAdmin(tk);
      localStorage.setItem("pt_admin", tk);
      setAuthed(true);
      await load(tk);
    } catch {
      if (!silent) toast.error("Ungültiger Token");
      localStorage.removeItem("pt_admin");
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  const addPartner = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await createPartner(form, token);
      setForm({ name: "", logo_url: "", website: "", category: "" });
      toast.success("Partner hinzugefügt");
      load(token);
    } catch {
      toast.error("Fehler beim Speichern");
    }
  };

  const removePartner = async (id) => {
    try {
      await deletePartner(id, token);
      toast.success("Partner gelöscht");
      load(token);
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const field = "w-full rounded-md border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-[#3BB0E5] transition-colors";

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F1F5F9] px-5" data-testid="admin-login">
        <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-8">
          <Logo />
          <div className="mt-6 flex items-center gap-2 text-sm font-semibold"><Lock size={16} color="var(--pt-blue)" /> Admin-Bereich</div>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin-Token" className={`${field} mt-4`} data-testid="admin-token-input" onKeyDown={(e) => e.key === "Enter" && tryLogin(token)} />
          <button onClick={() => tryLogin(token)} disabled={checking} data-testid="admin-login-btn" className="mt-4 w-full rounded-full bg-[#0A0F14] py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {checking ? <Loader2 size={16} className="animate-spin" /> : null} Anmelden
          </button>
          <Link to="/" className="mt-4 block text-center text-xs text-slate-400 hover:text-[#0A0F14]">← Zur Webseite</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo />
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0A0F14]"><ArrowLeft size={16} /> Webseite</Link>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8">
          {[["partners", "Partner"], ["gallery", `Galerie (${gallery.length})`], ["hotels", `Hotel-Kunden (${hotels.length})`], ["reviews", `Kundenstimmen${testimonials.filter((r) => !r.approved).length ? ` (${testimonials.filter((r) => !r.approved).length} neu)` : ""}`], ["contacts", `Anfragen (${contacts.length})`], ["settings", "Einstellungen"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className="rounded-full px-5 py-2 text-sm font-semibold transition-colors" style={{ background: tab === k ? "var(--pt-ink)" : "transparent", color: tab === k ? "#fff" : "#475569", border: "1px solid " + (tab === k ? "transparent" : "#E2E8F0") }}>
              {label}
            </button>
          ))}
        </div>

        {tab === "partners" && (
          <div className="grid lg:grid-cols-5 gap-8">
            <form onSubmit={addPartner} className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-6 h-fit" data-testid="partner-form">
              <h3 className="font-display font-bold text-lg mb-4">Partner hinzufügen</h3>
              <div className="space-y-3">
                <input required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="partner-name" />
                <input placeholder="Logo-URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={field} data-testid="partner-logo" />
                <input placeholder="Kategorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field} data-testid="partner-category" />
                <input placeholder="Webseite (https://)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={field} data-testid="partner-website" />
              </div>
              <button type="submit" data-testid="partner-add-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F39422] py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"><Plus size={16} /> Hinzufügen</button>
            </form>

            <div className="lg:col-span-3 space-y-3">
              {partners.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Noch keine Partner.</p>}
              {partners.map((p) => (
                <div key={p.id} className="flex items-center gap-4 rounded-md border border-slate-200 bg-white p-4" data-testid={`admin-partner-${p.id}`}>
                  {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-10 w-10 object-contain" /> : <span className="grid h-10 w-10 place-items-center rounded-md bg-slate-100"><Building2 size={18} color="var(--pt-blue)" /></span>}
                  <div className="flex-1"><div className="font-semibold text-sm">{p.name}</div>{p.category && <div className="text-xs text-slate-400">{p.category}</div>}</div>
                  <button onClick={() => removePartner(p.id)} data-testid={`delete-partner-${p.id}`} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-6 h-fit" data-testid="gallery-form">
              <h3 className="font-display font-bold text-lg mb-4">Projektfoto hochladen</h3>
              <input
                placeholder="Beschriftung (optional)"
                value={galleryCaption}
                onChange={(e) => setGalleryCaption(e.target.value)}
                className={field}
                data-testid="gallery-caption"
              />
              <label className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F39422] py-2.5 text-sm font-semibold text-white cursor-pointer transition-transform hover:scale-[1.02] ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-btn">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Wird hochgeladen…" : "Bild wählen & hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="gallery-file-input" disabled={uploading} />
              </label>
              <p className="mt-3 text-xs text-slate-400">JPG, PNG oder WEBP. Solange keine Fotos hochgeladen sind, zeigt die Website Beispielbilder.</p>
            </div>

            <div className="lg:col-span-3 space-y-3">
              {gallery.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Noch keine Fotos – aktuell werden Beispielbilder angezeigt.</p>}
              {gallery.map((g, i) => (
                <div key={g.id} className="flex items-center gap-4 rounded-md border border-slate-200 bg-white p-3" data-testid={`admin-gallery-${g.id}`}>
                  <img src={galleryImageUrl(g.id)} alt={g.caption || ""} className="h-14 w-20 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{g.caption || <span className="text-slate-400 font-normal">Ohne Beschriftung</span>}</div>
                    <div className="text-xs text-slate-400">Position {i + 1}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`gallery-up-${g.id}`} className="p-1.5 text-slate-400 hover:text-[#0A0F14] disabled:opacity-30 transition-colors"><ArrowUp size={16} /></button>
                    <button onClick={() => moveItem(i, 1)} disabled={i === gallery.length - 1} data-testid={`gallery-down-${g.id}`} className="p-1.5 text-slate-400 hover:text-[#0A0F14] disabled:opacity-30 transition-colors"><ArrowDown size={16} /></button>
                    <button onClick={() => removeGalleryItem(g.id)} data-testid={`delete-gallery-${g.id}`} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={17} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "hotels" && (
          <div className="grid lg:grid-cols-5 gap-8">
            <form onSubmit={addHotel} className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-6 h-fit" data-testid="hotel-form">
              <h3 className="font-display font-bold text-lg mb-4">Hotel-Kunde hinzufügen</h3>
              <div className="space-y-3">
                <input required placeholder="Hotelname *" value={hotelForm.name} onChange={(e) => setHotelForm({ ...hotelForm, name: e.target.value })} className={field} data-testid="hotel-name" />
                <input placeholder="Logo-URL" value={hotelForm.logo_url} onChange={(e) => setHotelForm({ ...hotelForm, logo_url: e.target.value })} className={field} data-testid="hotel-logo" />
                <input placeholder="Webseite (https://)" value={hotelForm.website} onChange={(e) => setHotelForm({ ...hotelForm, website: e.target.value })} className={field} data-testid="hotel-website" />
              </div>
              <button type="submit" data-testid="hotel-add-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F39422] py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"><Plus size={16} /> Hinzufügen</button>
              <p className="mt-3 text-xs text-slate-400">Tipp: Logo-URL leer lassen, dann wird der Hotelname als Text angezeigt.</p>
            </form>

            <div className="lg:col-span-3 space-y-3">
              {hotels.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Noch keine Hotel-Kunden.</p>}
              {hotels.map((h) => (
                <div key={h.id} className="flex items-center gap-4 rounded-md border border-slate-200 bg-white p-4" data-testid={`admin-hotel-${h.id}`}>
                  {h.logo_url ? <img src={h.logo_url} alt={h.name} className="h-10 w-16 object-contain" /> : <span className="grid h-10 w-10 place-items-center rounded-md bg-slate-100"><HotelIcon size={18} color="var(--pt-blue)" /></span>}
                  <div className="flex-1"><div className="font-semibold text-sm">{h.name}</div>{h.website && <div className="text-xs text-slate-400 truncate">{h.website}</div>}</div>
                  <button onClick={() => removeHotel(h.id)} data-testid={`delete-hotel-${h.id}`} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-3" data-testid="reviews-panel">
            {testimonials.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Noch keine Bewertungen. Kunden können sie direkt auf der Webseite eintragen.</p>}
            {testimonials.some((r) => !r.approved) && (
              <p className="text-xs text-[#475569] bg-[#FFF7ED] border border-[#FED7AA] rounded-md px-3 py-2" data-testid="reviews-pending-hint">
                Neue Bewertungen sind erst nach deiner Freigabe auf der Webseite sichtbar.
              </p>
            )}
            {[...testimonials].sort((a, b) => Number(a.approved) - Number(b.approved)).map((r) => (
              <div key={r.id} className={`rounded-md border bg-white p-5 ${r.approved ? "border-slate-200" : "border-[#FED7AA]"}`} data-testid={`admin-review-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                      {r.name}{r.role && <span className="text-xs font-normal text-[#475569]">{r.role}</span>}
                      {r.approved ? (
                        <span className="text-[10px] uppercase tracking-wide font-bold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Veröffentlicht</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wide font-bold text-[#C2410C] bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-2 py-0.5" data-testid={`review-pending-${r.id}`}>Ausstehend</span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={14} fill={n <= r.rating ? "#F39422" : "none"} color={n <= r.rating ? "#F39422" : "#CBD5E1"} strokeWidth={2} />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 mr-1">{new Date(r.created_at).toLocaleString()}</span>
                    {!r.approved && (
                      <button onClick={() => approveReview(r.id)} data-testid={`approve-review-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-105">
                        <Check size={14} /> Freigeben
                      </button>
                    )}
                    <button onClick={() => removeTestimonial(r.id)} data-testid={`delete-review-${r.id}`} className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={17} /></button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#475569] whitespace-pre-wrap border-l-2 pl-3" style={{ borderColor: "var(--pt-orange)" }}>{r.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Noch keine Anfragen.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="rounded-md border border-slate-200 bg-white p-5" data-testid={`contact-item-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display font-bold">{c.name} <span className="ml-2 text-xs font-normal uppercase text-[#3BB0E5]">{c.lang}</span></div>
                    <a href={`mailto:${c.email}`} className="text-sm text-[#475569] inline-flex items-center gap-1.5 mt-1"><Mail size={13} /> {c.email}</a>
                    {c.phone && <span className="text-sm text-[#475569] ml-3">{c.phone}</span>}
                  </div>
                  <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                {c.subject && <div className="mt-3 text-sm font-medium">{c.subject}</div>}
                <p className="mt-2 text-sm text-[#475569] whitespace-pre-wrap border-l-2 pl-3" style={{ borderColor: "var(--pt-orange)" }}>{c.message}</p>
                {!c.email_sent && <span className="mt-2 inline-block text-[11px] text-amber-600">E-Mail nicht versendet (in DB gespeichert)</span>}
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-md rounded-md border border-slate-200 bg-white p-6" data-testid="settings-panel">
            <div className="flex items-center gap-2 mb-1"><Wifi size={18} color="var(--pt-blue)" /><h3 className="font-display font-bold text-lg">Hero-Kennzahlen</h3></div>
            <p className="text-sm text-[#475569] mb-5">Diese Zahlen erscheinen im Hero-Bereich der Startseite (als „+18" bzw. „+106").</p>
            <label className="block text-sm font-medium mb-1.5">Realisierte Projekte</label>
            <input
              type="number"
              min="0"
              value={projectsCount}
              onChange={(e) => setProjectsCount(e.target.value)}
              className={field}
              data-testid="settings-projects-input"
            />
            <label className="block text-sm font-medium mt-4 mb-1.5">Montierte Access Points</label>
            <input
              type="number"
              min="0"
              value={apCount}
              onChange={(e) => setApCount(e.target.value)}
              className={field}
              data-testid="settings-ap-input"
            />
            <button onClick={saveAp} disabled={savingAp} data-testid="settings-ap-save" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0F14] py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              {savingAp ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
